import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {getPageGeneralPractitioners} from '../../general-practioner/GeneralPractitionerEndPoint'
import GeneralPractitionerForm from '../../general-practioner/GeneralPractitionerForm'
import {
    useGeneralPractitionerCRUD,
    usePageGeneralPractitioners,
} from '../../general-practioner/query-keys/GeneralPractitionerKeysManager'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {actionTableKeys} from '../../new-table/Constants'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {columnList} from './GeneralPractitionerTableConstants'
import {
    generalPractitionerEvent,
    generalPractitionerEventTypes,
    getGeneralPractitionerEventKey,
    onGeneralPractitionerTableChangeRemoveListener,
} from './broadcast/GeneralPractitionerEvent'

const StyledGeneralPractitionerDataTable = () => {
    useSignals()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {generalPractitionerManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.gp,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.gp)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteGeneralPractitioner} = useGeneralPractitionerCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deleteGeneralPractitioner(data)

            if (response === HTTP_STATUS_OK) {
                const {generalPractitionerKeys} = QueryKeyManager(
                    QueryKeyTypes.generalPractitioner,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(generalPractitionerKeys.pattern())
            }
        }
    }

    const handleAction = (type, rowValue) => {
        if (type === actionTableKeys.edit && rowValue?.id) {
            formRef.current?.showForm({...rowValue})
        } else if (type === actionTableKeys.delete && rowValue) {
            handleOnDelete(rowValue)
        }
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                generalPractitionerManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [generalPractitionerManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {generalPractitionerKeys} = QueryKeyManager(
            QueryKeyTypes.generalPractitioner,
            user?.id,
        )

        return generalPractitionerKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageGeneralPractitioners(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageGeneralPractitioners(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageGeneralPractitioners(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE
        generalPractitionerEvent.on(
            getGeneralPractitionerEventKey(
                generalPractitionerEventTypes.TABLE_CHANGE,
            ),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {generalPractitionerKeys} = QueryKeyManager(
                    QueryKeyTypes.generalPractitioner,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: generalPractitionerKeys.pattern(),
                })
            },
        )

        // PAGE
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(tableTypes.gp, filterEventTypes.advanced)}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(tableTypes.gp, filterEventTypes.advanced)
            onGeneralPractitionerTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.gp,
        )
    }
    //#endregion

    return (
        <PageContentWrapper className={'min-h-full-screen'}>
            <DatatableContext.Provider
                value={{
                    dataKey: 'id',
                    tableType: tableTypes.gp,
                    queryData: queryData,
                    isFetched: isFetched,
                    columnList: columnList,
                    tableToolbar: [tableToolbarType.tableControlToolbar],
                    tableActions: [actionTableKeys.delete],
                    minHeightFullScreen: true,

                    pageable: pageable.value,
                    onChangeSortColumn: handleChangeSortColumn,
                    onChangePageNumber: handleChangePageNumber,
                    onChangePageRows: handleChangePageRows,
                    onPageRowsHovered: handlePrefetchDataByRowsOption,

                    onDetailRowClick: handleOnDetailRowClick,
                    handleCheckPermissionByKey: handleCheckPermissionByKey,

                    initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                    handleClearFilterRequest: handleClearFilterRequest,
                    handleClearFilterRequestByKey:
                        handleClearFilterRequestByKey,
                    handleClearAdvancedFilterRequest:
                        handleClearAdvancedFilterRequest,

                    handleRetrieveExportData: handleRetrieveExportData,
                    handleChangeGlobalSearch: handleChangeGlobalSearch,

                    handleAddNew: handleAddNew,
                    handleAction: handleAction,
                }}>
                <StyledDataTableWrapper
                    headerOptions={{
                        title: 'General Practitioner Management',
                        subTitle:
                            'Manage the General Practitioner is a consultant in general practice.',
                    }}
                />

                <GeneralPractitionerForm ref={formRef} />
            </DatatableContext.Provider>
        </PageContentWrapper>
    )
}

export default StyledGeneralPractitionerDataTable
